import { PhoneInput } from "@/components/molecules/phone";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { usePublicTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import {
  ArrowLeftIcon,
  CalendarBlankIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckCircleIcon,
  SpinnerGapIcon,
  XIcon,
} from "@phosphor-icons/react";
import { type ReactNode, useEffect, useState } from "react";
import {
  type BookingCalendarFailure,
  type BookingCalendarTime,
  bookingFailureOf,
  useBookFromCalendar,
  useBookingCalendarOptions,
  useBookingCalendarPage,
} from "../../hooks/use-booking-calendar";
import {
  type UpdateCustomerError,
  usePublicCustomer,
  useUpdatePublicCustomer,
} from "../../hooks/use-public-customer";
import type { SessionMessage } from "../../types";
import { LoginDialog } from "../dialogs/LoginDialog";

/** A booking already made from this calendar — read back from the thread. */
export interface BookingCalendarBooked {
  /** Local wall-clock time, "2026-10-09T10:30:00". */
  start_time: string;
}

export const BOOKING_CALENDAR_TOOL = "show_booking_calendar";

/** A calendar the agent opened and that can be drawn: the tool ran, and it has an id. */
export const isBookingCalendar = (message: SessionMessage) =>
  message.type === "tool" &&
  message.tool?.name === BOOKING_CALENDAR_TOOL &&
  message.tool?.status === "success" &&
  !!message.tool?.call_id;

/** Every booking made from a calendar of this thread, by calendar. */
export const bookedFromCalendars = (messages: SessionMessage[]) => {
  const booked = new Map<string, BookingCalendarBooked>();
  for (const message of messages) {
    const callId = message.metadata?.booking_calendar;
    const start = message.tool?.input?.start_time;
    if (
      message.type === "tool" &&
      message.tool?.name === "create_calendar_event" &&
      message.tool.status === "success" &&
      typeof callId === "string" &&
      start
    ) {
      booked.set(callId, { start_time: start });
    }
  }
  return booked;
};

/**
 * The calendar the visitor is in the middle of: the last one of the thread, not booked
 * in, with nothing said since. That one opens over the chat by itself.
 */
export const activeBookingCalendar = (
  messages: SessionMessage[],
): string | undefined => {
  const last = [...messages].reverse().find(isBookingCalendar);
  const callId = last?.tool?.call_id;
  if (!last || !callId) return undefined;
  if (bookedFromCalendars(messages).has(callId)) return undefined;
  if (messages.some((m) => m.type === "user" && m.timestamp > last.timestamp)) {
    return undefined;
  }
  return callId;
};

// "details": what the booking still needs from the visitor — their name when the file has
// none, and in declarative mode their first and last name, phone and e-mail, which stand
// in for the SMS code.
type Step = "service" | "performer" | "calendar" | "details";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "2026-10-13" → a Date at noon UTC, so no timezone can move it to another day. */
const dayAt = (date: string) => new Date(`${date}T12:00:00Z`);

const formatDay = (
  date: string,
  locale: string,
  options: Intl.DateTimeFormatOptions,
) => {
  const format = (l: string) =>
    new Intl.DateTimeFormat(l, { ...options, timeZone: "UTC" }).format(
      dayAt(date),
    );
  try {
    return format(locale);
  } catch {
    return format("en");
  }
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The booking calendar the agent opened (`show_booking_calendar`), taking the whole
 * chat the way the "appel en cours" screen does: the thread and the input step aside
 * until the visitor books or closes it, and a card in the thread opens it again.
 *
 * The visitor books in it on their own, the model never in the loop: the service (unless
 * the agent named it, or there is only one), the team member (only after picking the
 * service from the list: with no list to pick from, it is "anyone"), a day and a time, then the
 * SMS code when they are not signed in — or, where the tenant settled for declared
 * contacts, their first and last name, phone and e-mail — and their name when their file
 * has none, each step with its way back.
 * The booking is the agent's own (`create_calendar_event`'s path, run by the backend on
 * this click), and its confirmation reaches the thread like any message. Every time shown
 * is the salon's clock (the page carries its timezone), never the visitor's: it is the
 * time they will walk in.
 *
 * A visitor who already has an appointment coming up is never offered a second one (the
 * booking would refuse it): the panel hands over to `onExisting`, which shows that one —
 * as soon as the options say so, or when the booking says so once the code told us who
 * they are.
 */
export function BookingCalendarPanel({
  publicId,
  callId,
  onClose,
  onExisting,
}: {
  publicId: string;
  callId: string;
  /** Closed by the visitor, or booked: either way the chat comes back. */
  onClose: () => void;
  /** They already have an appointment: the id when the options name it. */
  onExisting: (eventId?: string) => void;
}) {
  const { t, i18n } = usePublicTranslation();
  const locale = i18n.language || "fr";

  const options = useBookingCalendarOptions(publicId, callId, true);
  const [serviceId, setServiceId] = useState<string | null>(null);
  // `undefined`: not chosen yet; `null`: "anyone".
  const [performerId, setPerformerId] = useState<string | null | undefined>(
    undefined,
  );
  const [step, setStep] = useState<Step>("service");
  // The `from` of each page visited, so "back" returns exactly where the visitor was.
  const [pages, setPages] = useState<(string | undefined)[]>([undefined]);
  const [day, setDay] = useState<string | null>(null);
  const [selected, setSelected] = useState<BookingCalendarTime | null>(null);
  const [failure, setFailure] = useState<BookingCalendarFailure | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [details, setDetails] = useState({
    given_name: "",
    family_name: "",
    phone: "",
    email: "",
  });
  const [detailErrors, setDetailErrors] = useState<Record<string, string>>({});

  const data = options.data;
  const { data: customer } = usePublicCustomer(publicId);
  // A single service is no choice: the visitor starts past it, as if the agent had named it.
  const fixedService =
    data?.service_id ||
    (data?.services.length === 1 ? data.services[0].id : null);
  const service =
    data?.services.find((s) => s.id === (fixedService || serviceId)) || null;
  const performers = service?.performers || [];
  const fixedPerformer = data?.performer_id || null;
  const effectivePerformer = fixedPerformer || performerId || undefined;
  // The team member is a choice offered after the service list, never instead of it: with
  // no list to pick from (the agent named the service, or there is only one), the visitor
  // goes straight to the days, with whoever is free.
  const offersPerformers = !fixedService && !fixedPerformer;

  // Where the visitor starts: the service list when there is one, else the days.
  useEffect(() => {
    if (!data) return;
    if (data.existing_event_id) return onExisting(data.existing_event_id);
    setStep(
      !data.service_id && data.services.length !== 1 ? "service" : "calendar",
    );
  }, [data]);

  const page = useBookingCalendarPage(
    publicId,
    callId,
    {
      service_id: service?.id,
      performer_id: effectivePerformer,
      from: pages[pages.length - 1],
    },
    step === "calendar" || step === "details",
  );
  const days = page.data?.days || [];

  // Keep the day the visitor chose while it still has times, else the first one that does.
  useEffect(() => {
    if (!days.length) setDay(null);
    else if (!day || !days.some((d) => d.date === day)) setDay(days[0].date);
  }, [days, day]);

  const book = useBookFromCalendar(publicId, callId);
  const updateCustomer = useUpdatePublicCustomer(publicId);

  // What the details step asks for. Declarative only stands in for the code until the
  // visitor proved a contact: a signed-in visitor has nothing more to declare.
  const identified =
    !!customer?.phone_is_verified || !!customer?.email_is_verified;
  const declarative = data?.verification === "declarative" && !identified;
  // Declarative asks for the whole identity, both names included — with nothing verified,
  // it is all the salon has. Otherwise a name is only asked when the file has none.
  const askName =
    declarative || (!customer?.given_name && !customer?.family_name);

  // Whatever the file already holds comes prefilled: a field the visitor typed is kept.
  useEffect(() => {
    if (!customer) return;
    setDetails((d) => ({
      given_name: d.given_name || customer.given_name || "",
      family_name: d.family_name || customer.family_name || "",
      phone: d.phone || customer.phone || "",
      email: d.email || customer.email || "",
    }));
  }, [customer]);

  const resetCalendar = () => {
    setPages([undefined]);
    setSelected(null);
    setFailure(null);
  };

  const chooseService = (id: string) => {
    setServiceId(id);
    setPerformerId(undefined);
    resetCalendar();
    const next = data?.services.find((s) => s.id === id);
    setStep(
      !offersPerformers || (next?.performers.length || 0) <= 1
        ? "calendar"
        : "performer",
    );
  };

  // Back: one step at a time, never into a step the agent settled.
  const back =
    step === "details"
      ? () => setStep("calendar")
      : step === "calendar" && offersPerformers && performers.length > 1
        ? () => setStep("performer")
        : (step === "calendar" || step === "performer") && !fixedService
          ? () => setStep("service")
          : undefined;

  const submit = async () => {
    if (!service || !selected) return;
    setFailure(null);
    try {
      await book.mutateAsync({
        service_id: service.id,
        start: selected.start,
        ...(effectivePerformer ? { performer_id: effectivePerformer } : {}),
      });
      onClose();
    } catch (error) {
      const reason = bookingFailureOf(error);
      if (reason === "sign_in_required") return setLoginOpen(true);
      if (reason === "already_booked") return onExisting();
      if (reason === "name_required" || reason === "contact_required") {
        return setStep("details");
      }
      setFailure(reason);
      if (reason === "refused") {
        setSelected(null);
        void page.refetch();
      }
    }
  };

  const trimmed = {
    given_name: details.given_name.trim(),
    family_name: details.family_name.trim(),
    phone: details.phone.trim(),
    email: details.email.trim(),
  };
  const detailsReady =
    (!askName || !!trimmed.given_name) &&
    (!declarative ||
      (!!trimmed.family_name && !!trimmed.phone && !!trimmed.email));

  const clearDetailError = (key: string) =>
    setDetailErrors((errors) => {
      const next = { ...errors };
      delete next[key];
      return next;
    });

  const saveDetails = async () => {
    if (!detailsReady) return;
    setFailure(null);
    if (declarative && !EMAIL.test(trimmed.email)) {
      setDetailErrors({ email: t("confirm_info.invalid_email") });
      return;
    }
    setDetailErrors({});
    try {
      await updateCustomer.mutateAsync({
        ...(askName
          ? { given_name: trimmed.given_name, family_name: trimmed.family_name }
          : {}),
        // A contact the channel proved is not the visitor's to retype: left out.
        ...(declarative && !customer?.phone_is_locked
          ? { phone: trimmed.phone }
          : {}),
        ...(declarative && !customer?.email_is_locked
          ? { email: trimmed.email }
          : {}),
      });
    } catch (error) {
      const feedback = (error as UpdateCustomerError)?.feedback;
      if (feedback && Object.keys(feedback).length) {
        setDetailErrors(
          Object.fromEntries(
            Object.entries(feedback).map(([field, message]) => [
              field,
              message === "contact_locked"
                ? t("confirm_info.contact_locked")
                : message,
            ]),
          ),
        );
      } else {
        setFailure("failed");
      }
      return;
    }
    setStep("calendar");
    await submit();
  };

  const current = days.find((d) => d.date === day);
  const slotLabel =
    selected && current
      ? `${formatDay(current.date, locale, { weekday: "short", day: "numeric", month: "short" })} · ${selected.time}`
      : "";
  const performerName = effectivePerformer
    ? performers.find((p) => p.id === effectivePerformer)?.name ||
      page.data?.performer?.name
    : undefined;

  const subtitle =
    step === "service"
      ? t("booking_calendar.pick_service")
      : [
          service?.name,
          step === "performer"
            ? t("booking_calendar.pick_performer")
            : performerName
              ? t("booking_calendar.with", { name: performerName })
              : service?.duration
                ? t("booking_calendar.duration", { minutes: service.duration })
                : "",
        ]
          .filter(Boolean)
          .join(" · ");

  let body: ReactNode;
  if (options.isError || (page.isError && !page.data)) {
    body = (
      <p className="py-10 text-center text-sm text-muted-foreground">
        {t("booking_calendar.error")}
      </p>
    );
  } else if (!data) {
    body = <PanelSkeleton />;
  } else if (step === "service") {
    body = (
      <div className="divide-y">
        {data.services.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => chooseService(s.id)}
            className="flex w-full items-center gap-3 py-3 text-left text-sm hover:text-primary"
          >
            <span className="min-w-0 flex-1 truncate font-medium">
              {s.name}
            </span>
            <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
              {s.duration > 0 &&
                t("booking_calendar.duration", { minutes: s.duration })}
              {s.price ? ` · ${s.price} €` : ""}
            </span>
            <CaretRightIcon
              size={13}
              className="shrink-0 text-muted-foreground"
            />
          </button>
        ))}
      </div>
    );
  } else if (step === "performer") {
    body = (
      <div className="grid grid-cols-2 gap-2">
        {[{ id: null, name: t("booking_calendar.anyone") }, ...performers].map(
          (p) => (
            <button
              key={p.id ?? "anyone"}
              type="button"
              onClick={() => {
                setPerformerId(p.id);
                resetCalendar();
                setStep("calendar");
              }}
              className={cn(
                "truncate rounded-lg border px-3 py-3 text-sm transition-colors hover:border-primary/50",
                p.id === null
                  ? "border-primary/40 bg-primary/5 font-medium text-primary"
                  : "bg-background",
              )}
            >
              {p.name}
            </button>
          ),
        )}
      </div>
    );
  } else if (step === "details") {
    const field = (key: keyof typeof details) => ({
      value: details[key],
      onChange: (e: { target: { value: string } }) => {
        setDetails((d) => ({ ...d, [key]: e.target.value }));
        clearDetailError(key);
      },
      "aria-invalid": !!detailErrors[key],
    });
    const error = (key: string) =>
      detailErrors[key] && (
        <p className="text-xs text-destructive">{detailErrors[key]}</p>
      );
    body = (
      <form
        id="booking-calendar-details"
        onSubmit={(e) => {
          e.preventDefault();
          void saveDetails();
        }}
        className="space-y-3"
      >
        <p className="text-sm text-muted-foreground">
          {t(
            declarative
              ? "booking_calendar.contact_hint"
              : "booking_calendar.name_hint",
            { slot: slotLabel },
          )}
        </p>
        {askName && (
          <>
            <Input
              autoFocus
              {...field("given_name")}
              placeholder={t("booking_calendar.given_name")}
              autoComplete="given-name"
            />
            {error("given_name")}
            <Input
              {...field("family_name")}
              placeholder={t("booking_calendar.family_name")}
              autoComplete="family-name"
            />
            {error("family_name")}
          </>
        )}
        {declarative && (
          <>
            <PhoneInput
              placeholder={t("booking_calendar.phone")}
              value={details.phone}
              onChange={(value) => {
                setDetails((d) => ({ ...d, phone: value || "" }));
                clearDetailError("phone");
              }}
              aria-invalid={!!detailErrors.phone}
              disabled={!!customer?.phone_is_locked}
              autoComplete="tel"
            />
            {error("phone")}
            <Input
              {...field("email")}
              type="email"
              placeholder={t("booking_calendar.email")}
              autoComplete="email"
              disabled={!!customer?.email_is_locked}
            />
            {error("email")}
          </>
        )}
      </form>
    );
  } else if (!page.data) {
    body = <PanelSkeleton />;
  } else {
    body = (
      <>
        {/* Days: a strip the visitor scrolls, the page's arrows on each side. */}
        <div className="flex items-center gap-1.5">
          <NavButton
            label={t("booking_calendar.previous")}
            disabled={pages.length <= 1 || page.isFetching}
            onClick={() => {
              setPages((p) => p.slice(0, -1));
              setSelected(null);
            }}
          >
            <CaretLeftIcon size={13} weight="bold" />
          </NavButton>
          <div
            className={cn(
              "flex min-w-0 flex-1 gap-1.5 overflow-x-auto py-0.5 [scrollbar-width:none]",
              page.isFetching && "opacity-60",
            )}
          >
            {days.length === 0 && (
              <p className="flex-1 py-2 text-center text-xs text-muted-foreground">
                {t("booking_calendar.empty_page")}
              </p>
            )}
            {days.map((d) => (
              <button
                key={d.date}
                type="button"
                data-calendar-day
                title={formatDay(d.date, locale, {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}
                onClick={() => {
                  setDay(d.date);
                  setSelected(null);
                }}
                className={cn(
                  "flex w-12 shrink-0 flex-col items-center rounded-lg border py-1.5 transition-colors",
                  d.date === day
                    ? "border-primary bg-primary text-primary-foreground"
                    : "bg-background hover:border-primary/50",
                )}
              >
                <span className="text-[10px] uppercase opacity-80">
                  {formatDay(d.date, locale, { weekday: "short" }).replace(
                    ".",
                    "",
                  )}
                </span>
                <span className="text-base font-semibold leading-tight">
                  {formatDay(d.date, locale, { day: "numeric" })}
                </span>
                <span className="text-[10px] opacity-80">
                  {formatDay(d.date, locale, { month: "short" }).replace(
                    ".",
                    "",
                  )}
                </span>
              </button>
            ))}
          </div>
          <NavButton
            label={t("booking_calendar.next")}
            disabled={!page.data.next || page.isFetching}
            onClick={() => {
              setPages((p) => [...p, page.data?.next || undefined]);
              setSelected(null);
            }}
          >
            <CaretRightIcon size={13} weight="bold" />
          </NavButton>
        </div>

        {current && (
          <div className="mt-4 grid grid-cols-4 gap-2">
            {current.times.map((time) => (
              <button
                key={time.start}
                type="button"
                onClick={() => {
                  setSelected(time);
                  setFailure(null);
                }}
                className={cn(
                  "rounded-lg border py-2 text-sm tabular-nums transition-colors",
                  selected?.start === time.start
                    ? "border-primary bg-primary text-primary-foreground"
                    : "bg-background hover:border-primary/50",
                )}
              >
                {time.time}
              </button>
            ))}
          </div>
        )}
      </>
    );
  }

  const busy = book.isPending || updateCustomer.isPending;

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-background animate-fade-in">
      {/* What is being booked, the way back, and the way out. */}
      <div className="flex items-center gap-2 border-b px-4 py-3">
        {back ? (
          <button
            type="button"
            onClick={() => {
              setFailure(null);
              back();
            }}
            aria-label={t("booking_calendar.back")}
            title={t("booking_calendar.back")}
            className="-ml-1 flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ArrowLeftIcon size={16} weight="bold" />
          </button>
        ) : (
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CalendarBlankIcon size={16} weight="duotone" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{t("booking_calendar.title")}</p>
          <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("booking_calendar.close")}
          title={t("booking_calendar.close")}
          className="flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <XIcon size={16} weight="bold" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{body}</div>

      {(failure ||
        (selected && (step === "calendar" || step === "details"))) && (
        <div className="space-y-2 border-t px-4 py-3">
          {failure && (
            <p className="text-xs text-destructive">
              {t(`booking_calendar.failure_${failure}`, {
                defaultValue: t("booking_calendar.failure_failed"),
              })}
            </p>
          )}
          {selected && (step === "calendar" || step === "details") && (
            <button
              type="button"
              disabled={busy || (step === "details" && !detailsReady)}
              onClick={() =>
                void (step === "details" ? saveDetails() : submit())
              }
              className="flex w-full items-center justify-center gap-2 rounded-[var(--chat-radius,0.75rem)] bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-60"
            >
              {busy && <SpinnerGapIcon size={15} className="animate-spin" />}
              {t("booking_calendar.book", { slot: slotLabel })}
            </button>
          )}
        </div>
      )}

      {/* Not signed in, and the tenant wants the code: the code first, then the booking
          carries on by itself. No "continue by phone" here — that hands the conversation
          to the agent. */}
      <LoginDialog
        publicId={publicId}
        open={loginOpen}
        onOpenChange={setLoginOpen}
        onSuccess={() => {
          setLoginOpen(false);
          void submit();
        }}
      />
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex gap-1.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-14 w-12 rounded-lg" />
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => (
          <Skeleton key={i} className="h-9 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

/**
 * What stays in the thread: the booking made from this calendar, or the way back into
 * it. The calendar itself opens over the chat (`BookingCalendarPanel`).
 */
export function BookingCalendarCard({
  booked,
  onOpen,
}: {
  booked?: BookingCalendarBooked;
  /** Absent once booked: a second booking from the same calendar is not offered. */
  onOpen?: () => void;
}) {
  const { t, i18n } = usePublicTranslation();
  const locale = i18n.language || "fr";
  const label = booked
    ? `${capitalize(
        formatDay(booked.start_time.slice(0, 10), locale, {
          weekday: "short",
          day: "numeric",
          month: "short",
        }),
      )} · ${booked.start_time.slice(11, 16)}`
    : null;

  const content = (
    <>
      {label ? (
        <CheckCircleIcon
          size={16}
          weight="fill"
          className="shrink-0 text-primary"
        />
      ) : (
        <CalendarBlankIcon
          size={16}
          weight="duotone"
          className="shrink-0 text-primary"
        />
      )}
      <span className="min-w-0 flex-1 truncate text-left">
        {label
          ? t("booking_calendar.booked", { slot: label })
          : t("booking_calendar.collapsed")}
      </span>
      {!label && (
        <span className="shrink-0 text-xs font-medium text-primary">
          {t("booking_calendar.open")}
        </span>
      )}
    </>
  );

  const className =
    "flex w-[min(21rem,calc(100vw-5rem))] items-center gap-2 rounded-[var(--chat-radius,1rem)] border bg-card px-3 py-2 text-sm text-card-foreground";

  return label || !onOpen ? (
    <div className={className}>{content}</div>
  ) : (
    <button
      type="button"
      onClick={onOpen}
      className={cn(className, "hover:border-primary/50")}
    >
      {content}
    </button>
  );
}

function NavButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-7 shrink-0 items-center justify-center rounded-full border bg-background text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
    >
      {children}
    </button>
  );
}
